import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import CommentActions from "./CommentActions";
import CommentsThread from "./thread/CommentsThread";
import {
  deleteComments,
  fetchComment,
  validateComments,
} from "../../api/legacy/CommentAPI";
import { updateUser } from "../../api/legacy/UserAPI";
import "../../css/admin/comments.scss";
import useAbility from "../../hooks/useAbility";
import ConfirmModal from "../modal/ConfirmModal";
import { PROJECTS_ROUTE } from "../routes/routes";
import ContainerHeader from "../utils/ContainerHeader";
import FormNavigationHeader from "../utils/FormNavigationHeader";

import type { JSX } from "react";
import type { AnyAction } from "redux";

import type { DetailedComment } from "./comment.types";
import type { CommentActionType } from "./CommentActions";
import type { AppState } from "../../reducers";
import type { ModalActions } from "../modal/Modal";

interface RouteParams {
  id: string;
  projectSlug: string;
}

function Comment(): JSX.Element | null {
  const { t } = useTranslation("editor-comments");
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { id, projectSlug } = useParams<RouteParams>();
  const history = useHistory();

  const [comment, setComment] = useState<DetailedComment | null>(null);
  const [actionType, setActionType] = useState<CommentActionType | null>(null);

  const { canAccess } = useAbility();

  const { project: canAccessProject } = canAccess(orgaOptions);

  const dispatch = useDispatch();

  const popupText: Record<CommentActionType, { title: string; text: string }> =
    {
      block: {
        title: t("profile:blockUser"),
        text: t("popup:confirm_block", {
          user: `${comment?.user?.first_name} ${comment?.user?.last_name}`,
        }),
      },
      unblock: {
        title: t("profile:unblockUser"),
        text: t("popup:confirm_unblock", {
          user: `${comment?.user?.first_name} ${comment?.user?.last_name}`,
        }),
      },
      valid: {
        title: t("popup:validTitle"),
        text: t("popup:confirm_show_valid"),
      },
      delete: {
        title: t("popup:deleteTitle"),
        text: t("popup:confirm_show_delete"),
      },
    };

  const breadcrumbs = [
    {
      name: t("editor-menu:community"),
      url: "/comments",
    },
    {
      name: t("sectionTitle"),
      url: "/comments",
    },
    {
      name: t("editor-utils:edit"),
    },
  ];

  const getComment = (): void => {
    fetchComment(token, projectSlug, id, (resp: DetailedComment) =>
      setComment(resp),
    );
  };

  useEffect(() => {
    getComment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirm = (): void => {
    if (!comment) return;

    switch (actionType) {
      case "block":
      case "unblock":
        dispatch(
          updateUser(
            comment.user?.uuid,
            { block_comments: !comment.user?.block_comments },
            token,
            projectSlug,
            t,
            () => {
              setActionType(null);
              getComment();
            },
          ) as AnyAction,
        );
        break;
      case "valid":
        dispatch(
          validateComments([comment.uuid], token, projectSlug, t, 1, () => {
            setActionType(null);
            getComment();
          }) as AnyAction,
        );
        break;
      case "delete":
        dispatch(
          deleteComments([comment.uuid], token, projectSlug, t, 1, () => {
            history.push(`${PROJECTS_ROUTE}/${projectSlug}/comments`);
          }) as AnyAction,
        );
        break;

      default:
        break;
    }
  };

  const getConfirmButtonVariant = (): "primary" | "delete" => {
    switch (actionType) {
      case "block":
      case "delete":
        return "delete";
      case "unblock":
      case "valid":
        return "primary";
      default:
        return "primary";
    }
  };

  const modalActions: ModalActions[] = [
    {
      label: t("popup:cancel"),
      onClick: () => setActionType(null),
      buttonVariant: "secondary",
    },
    {
      label: t("popup:submit"),
      onClick: onConfirm,
      buttonVariant: getConfirmButtonVariant(),
    },
  ];

  if (!comment) return null;

  return (
    <div className="editor-comment-container editor-has-breadcrumbs">
      <FormNavigationHeader breadcrumbs={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        hasLikes={comment.comment_likes?.length}
        hasReports={comment.comment_abuses?.length}
        isVerified={comment.valid}
        item={comment}
        labelAttr="comment"
      />
      <CommentActions
        comment={comment}
        createAction={(type: CommentActionType) => setActionType(type)}
        userAbility={canAccessProject.usersAnalytics}
      />
      <CommentsThread item={comment} />
      <ConfirmModal
        actions={modalActions}
        isOpen={!!actionType}
        onClose={(): void => setActionType(null)}
        size="md"
        title={actionType ? popupText[actionType]?.title : ""}
      >
        {actionType ? popupText[actionType]?.text : ""}
      </ConfirmModal>
    </div>
  );
}

export default Comment;
